// 参加者の 8 ボタングリッド。トグル ON/OFF (spec §4.1)。
// 自動 OFF はしない (タイムアウトなし)。ホストの一括リセット時のみサーバ側で OFF が記録される。
// クライアントの local 状態はホスト reset の影響を受けない (= 押下した参加者本人の画面では押下中表示が残る)
// ことに注意。完全な参加者反映は WebSocket / SSE が要るため M11 以降に検討。

import { useCallback, useState, type ReactElement } from 'react';
import { t, type Locale } from '@solo-webinar-copilot/i18n';
import type { ButtonType } from '@solo-webinar-copilot/shared';
import { BUTTONS } from '../lib/buttons.ts';
import { postEvent } from '../lib/api-client.ts';
import type { AppContext } from '../lib/zoom-sdk.ts';

export interface ButtonGridProps {
  ctx: AppContext;
  locale: Locale;
}

export function ButtonGrid({ ctx, locale }: ButtonGridProps): ReactElement {
  const [active, setActive] = useState<Set<ButtonType>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const toggle = useCallback(
    async (type: ButtonType) => {
      const willBeOn = !active.has(type);
      // 楽観更新: 先に UI を変えてから送信。失敗したら戻す。
      setActive((prev) => {
        const next = new Set(prev);
        if (willBeOn) next.add(type);
        else next.delete(type);
        return next;
      });
      const r = await postEvent(ctx, { type, state: willBeOn ? 'on' : 'off' });
      if (!r.ok) {
        setActive((prev) => {
          const next = new Set(prev);
          if (willBeOn) next.delete(type);
          else next.add(type);
          return next;
        });
        setError(r.error);
        return;
      }
      setError(null);
    },
    [active, ctx],
  );

  return (
    <div data-testid="button-grid">
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
        }}
      >
        {BUTTONS.map((b) => {
          const isOn = active.has(b.type);
          return (
            <li key={b.type}>
              <button
                type="button"
                onClick={() => void toggle(b.type)}
                aria-pressed={isOn}
                data-testid={`btn-${b.type}`}
                data-active={isOn ? 'true' : 'false'}
                style={{
                  width: '100%',
                  padding: '12px 8px',
                  fontSize: 14,
                  textAlign: 'left',
                  background: isOn ? '#2563eb' : '#f3f4f6',
                  color: isOn ? '#fff' : '#111',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                <span aria-hidden="true" style={{ marginRight: 6 }}>
                  {b.icon}
                </span>
                {t(b.labelKey, locale)}
              </button>
            </li>
          );
        })}
      </ul>
      {error ? (
        <p role="alert" data-testid="grid-error">
          {t('in_client.error.network', locale)}: {error}
        </p>
      ) : null}
    </div>
  );
}
